<template>
    <div id="modal-target" />
    <Toaster position="top-right" :duration="5000" theme="dark" rich-colors close-button />

    <ReauthModal v-if="ready" />
    <router-view v-show="ready" />
    <Transition name="preloader-fade">
        <Preloader v-if="!ready" :message="loadingMessage" :error="loadError" />
    </Transition>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent, ref } from 'vue';
import { Toaster } from 'vue-sonner';
import 'vue-sonner/style.css';
import AuthenticationService from '~/api/services/client/authentication';
import SettingsService from '~/api/services/client/settings';
import { loadLanguage, Logger, Router, setCurrentLanguage } from '~/core';
import { useSettingsStore, useUserStore } from '~/stores';
import Preloader from '~/views/Preloader.vue';

const ReauthModal = defineAsyncComponent(() => import('~/views/account/ReauthModal.vue'));

export default defineComponent({
    components: { Toaster, Preloader, ReauthModal },
    setup() {
        // We'd use <Suspense> but as this is the parent component, it won't work :(
        const ready = ref(false);
        const loadError = ref(false);
        const loadingMessage = ref('Booting');
        const userStore = useUserStore();
        const settingsStore = useSettingsStore();

        // Kick off the English locale download in parallel with the
        // user + settings API calls. Previously this ran *after* both
        // finished, adding the full locale-chunk download to the
        // critical path before the first route could render. Since
        // loadLanguage doesn't depend on user data, there's no reason
        // to sequence it.
        const languageReady = loadLanguage('en');

        Promise.all([AuthenticationService.initializeUser(), SettingsService.initializeSettings(), languageReady])
            .then(async () => {
                const preferredLocale = userStore.data?.preferences?.language || settingsStore.data?.default_locale;
                if (preferredLocale) await setCurrentLanguage(preferredLocale);

                // Wait for the initial route's lazy chunk + middlewares to finish so the
                // real UI is mounted underneath before we fade out the skeleton. Avoids
                // the brief blank flash between unmounting the preloader and the route
                // component finishing its async load.
                await Router.isReady();

                ready.value = true;

                if (userStore.loggedIn) {
                    void import('~/api/services/client/notifications').then(({ default: NotificationsService }) =>
                        NotificationsService.initializeNotifications(),
                    );
                }
            })
            .catch((err) => {
                loadError.value = true;
                loadingMessage.value = 'Something went wrong. Try refreshing the page or come back later.';

                Logger.error('App', 'Initialization failed', err);
            });

        return {
            ready,
            loadError,
            loadingMessage,
        };
    },
});
</script>

<style>
.preloader-fade-leave-active {
    transition: opacity 0.25s ease-out;
}

.preloader-fade-leave-to {
    opacity: 0;
}
</style>
